// classes and helpers for QuoteSheetCompareGrid
// we will reuse many things from quotesheet-reply-helpers since those are similar... in both we're just displaying items/variants, not making them

import { 
    //QsItemForReply, 
    //QsItemVariantForReply, 
    QuoteSheetReplyBid 
} from '../quotesheets/quotesheet-reply-helpers';
//import { LinkedProduct } from './quote-sheet-common-helpers';
import Vue from 'vue';  // needed for components used in the grid
import { vendorColsAll } from '../quotesheets/quote-sheet-vendor-cols';
//import min from 'lodash/min';
//import max from 'lodash/max';
import mean from 'lodash/mean';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
//import pickBy from 'lodash/pickBy';
import isString from 'lodash/isString';
import { 
    //FormatDateTimeLocal,
    //FormatDateHelper,
    FormatFileSizeHelper, 
    hasValueOrZero,
} from '../../js/filters-helpers';
import { formatSmartFormValue } from '../smartforms/smartforms-helpers';

// FOR QUOTESHEETS

const grandTotalVariantId = 'GRAND_TOTAL_VARIANT';  // export this, use in grid component too

// how this works... we first instantiate with item/variant, then we use addBid() later to add individual bids from each reply
// bids will be inside, keyed by the QuoteSheetReply.id -- why that?  so we can use the same grid to look at multiple replies from same company, or replies from different companies
class CompareGridRow {  // THE ACTUAL DATA OBJECT IN THE GRID
    constructor({ // import all these things from quotesheet-reply-helpers
        item,  // REQUIRED pass in instance of QsItemForReply -- linked_product will be nested inside
        variant,  // REQUIRED pass in instance of QsItemVariantForReply
        replies,  // NEW June 14 2022 - pass in all replies -- the bids are inside 'variant_bid_map' - keyed by variant_id
    }) {
        this.id = variant.id;  // then use gridOptions.getRowNodeId() to use this as the ID in the grid too
        this.isGrandTotalRow = this.id === grandTotalVariantId;  // IS THIS THE GRAND TOTAL ROW???
        this.groupId = item.id;  // bring this to top level to make it clear how grouping happens
        this.item = Object.freeze(item);  // instance of QsItemForReply - freeze it to prevent modifications - linked_product will be nested inside if any
        this.variant = Object.freeze(variant);  // instance of QsItemVariantForReply - freeze it to prevent modifications
        // we will not add bids in constructor, but we'll add top level key for 'bids'
        this.bids = {};  // this should be an OBJECT, keys will be the QuoteSheetReply.id, values will be the bid objects
        this.bidsForAnalysis = {};  // only use these for analysis (when doing min/max/mean)
        // build up bids and bidsForAnalysis
        replies.forEach(reply => {
            const reply_id = reply.id;
            const bid_map = reply.variant_bid_map ?? {};  // use nullish coalescing to make sure we have an object here so we don't throw error below
            const isLatest = reply.is_latest;  // is this the LATEST reply from the company who submitted it?  we only use these for analysis cols
            let bidData;
            if (this.isGrandTotalRow) {
                // is this the GRAND_TOTAL row? - make a dummy data/row to show 
                const grandTotalObj = reply.variant_bid_grand_total;  // has nested 'amount' and 'currency' - IN BASE CURRENCY FOR QUOTE SHEET
                const quotedGrandTotalObj = reply.variant_bid_quoted_grand_total;  // in QUOTED currency, quoted by supplier
                bidData = {
                    //price: grandTotalObj.amount ?? null,
                    price_currency: grandTotalObj.currency ?? '',  // must have this -- its the currency we use for subtotal... but it comes from our grand_total object
                    subtotal: grandTotalObj.amount ?? null,  // should we only use this, not price?  makes more sense visually
                    qs_item_variant_id: grandTotalVariantId,  // the constant above
                    // AND add in quoted_subtotal / quoted_currency 
                    quoted_price_currency: quotedGrandTotalObj.currency ?? '',  // the quoted_subtotal column expects this in the bid rows, since that's where the quoted_price currency is held on reply bids
                    quoted_subtotal: quotedGrandTotalObj.amount ?? null,
                };
            } else {
                // if NOT the grand_total row... just a normal row for one bid line
                bidData = bid_map[this.id];
            }
            const bidObj = bidData ? new QuoteSheetReplyBid(bidData) : null;
            this.bids[reply_id] = bidObj;  // default to {} if bidObj was null????
            if (isLatest) {
                this.bidsForAnalysis[reply_id] = bidObj;
            }
        });
        // THINGS THAT USED TO BE GETTERS
        this.bidArray = Object.values(this.bids);
        this.priceCurrenciesValid = this.getPriceCurrenciesValid();
        this.priceCurrency = this.getPriceCurrency();
        this.analysisArray = this.getAnalysisArray();  // array of objects { replyId, price, price_currency }
        this.hasMultiplePricesForAnalysis = !!this.analysisArray && this.analysisArray.length > 1;
        // CALCULATE ANALYSIS THINGS
        const analysisKey = this.isGrandTotalRow ? 'subtotal' : 'price';
        const minAnalysisReply = minBy(this.analysisArray, analysisKey);  // returns undefined if array is empty, which is OK
        this.minReplyId = minAnalysisReply ? minAnalysisReply.replyId : undefined;
        this.minBidPrice = minAnalysisReply ? minAnalysisReply[analysisKey] : undefined;
        //this.minBidPrice = this.valueStringFormatter(this.minValue);  // using the same name as quotesheets for now
        const maxAnalysisReply = maxBy(this.analysisArray, analysisKey);  // returns undefined if array is empty, which is OK
        this.maxReplyId = maxAnalysisReply ? maxAnalysisReply.replyId : undefined;
        this.maxBidPrice = maxAnalysisReply ? maxAnalysisReply[analysisKey] : undefined;
        //this.maxBidPrice = this.valueStringFormatter(this.maxValue);  // using the same name as quotesheets for now
        // MEAN - updated Oct 11 2024 - make sure to filter out any nulls or undefineds from the array before calculating mean
        this.meanBidPrice = this.analysisArray.length < 1 ? undefined : mean(this.analysisArray.map(aObj => aObj[analysisKey]).filter(v => (v !== null && v !== undefined)));
        //this.meanBidPrice = this.valueStringFormatter(this.meanValue);  // using the same name as quotesheets for now
        this.canShowChart = this.variant.enable_price || this.isGrandTotalRow;  // our logic in the compare grid can handle grand totals or price only for now
    }
    // GETTERS
    // TODO - make all this more efficient by doing it all in the constructor, passing in more stuff -- then you don't have to recalculate getters all the time
    get itemName() {
        return this.item.itemName;  // uses getter inside nested item, gets from item or from linked_product as appropriate
    }
    get partNumber() {
        return this.item.partNumber;  // uses getter inside nested item, gets from item or from linked_product as appropriate
    }
    get rowLabel() {
        // friendly formatting of the label for this row (item/linked_product / variant)
        if (this.isGrandTotalRow) {
            return 'GRAND TOTAL';
        }
        const itemName = this.itemName;
        const partNumber = this.partNumber;
        const variantDesc = this.variant.description;
        const itemPart = itemName && partNumber ? `${itemName} (${partNumber})` : itemName || partNumber || '';
        return [itemPart, variantDesc].filter(Boolean).join(' - ');
    }
    get enabledVendorColObjs() { // return an array of full vendorCol objects (from canonical array) -- only the ones that are turned on for THIS SPECIFIC ROW
        // loop through canonical vendorColsAll, see which ones are enabled in this variant
        return vendorColsAll.filter(vcol => this.variant[vcol.enableKey]);
    }
    get enabledVendorColKeys() { // return an array of plainKey vendor cols -- only the ones that are turned on for THIS SPECIFIC ROW
        // just do a .map() from this.enabledVendorColObjs -- DRY
        return this.enabledVendorColObjs.map(vcol => vcol.plainKey);  // Array of plainKeys ['price', 'leadtime', ...]
    }
    get requiredVendorColKeys() {  // OF THE ENABLED vendorCols for THIS LINE, which ones are REQUIRED (used for validation of THIS LINE)
        // uses and returns array of plainKey strings such as ['price', 'leadtime', ...]
        return this.enabledVendorColObjs.filter(vcol => vcol.required).map(vcol => vcol.plainKey);
    }
    // METHODS 
    getPriceCurrenciesValid() {  // use all bids here, not just ones for analysis
        // returns Boolean, check if the price_currency matches for all bids (only checks if they have a non-null price)
        // NEW March 1 2022 - careful -- there may be nobids -- those SHOULD have null as the value already, so they will have .hasValidPrice=false
        //                  - also, need to handle handle having ALL BIDS as nobids -- in case there are no bids left after filters - that was causing error before
        //                  - if there are no bids left after filter, don't log error.... return true in that case, since that on its own certainly does not mean there's a problem
        // HANDLE both GRAND TOTAL rows and NORMAL rows
        let bidsToCheck = [];
        if (this.isGrandTotalRow) {
            bidsToCheck = this.bidArray.filter(bid => hasValueOrZero(bid.subtotal));  // only those that have a valid subtotal
        } else {
            // for NORMAL rows
            bidsToCheck = this.bidArray.filter(bid => bid?.hasValidPrice);
        }
        const validBidPricesCurrencies = bidsToCheck.map(bid => bid.price_currency);  // array of currency codes for bids with a non-null price/subtotal
        // March 1 2022 -- instead of checking for length === 1, we'll check that it is 0 or 1 -- because if no valid bids (everyone nobid) then we'll have empty array
        const allCurrenciesSame = uniq(validBidPricesCurrencies).length <= 1;
        if (!allCurrenciesSame) {
            console.error(`bid price_currency mismatch in QuoteSheetItemVariant ID ${this.id}`);
        }
        return allCurrenciesSame;
    }
    getPriceCurrency() {  // use all bids here, not just ones for analysis
        // returns the 3-digit currency code for all the prices in bids -- should be the same for all
        // if they are different, it will return empty string
        // this is for convenience since otherwise you have price_currency buried deep in each bid
        // NEW March 1 2022 - we changed priceCurrenciesValid to return true if there are no valid bids (ie: everyone nobid)
        //                  - so now we must also handle empty bid array after filters
        if (!this.priceCurrenciesValid) {
            return '';
        }
        // OLD - assumes we always have one valid bid... not always though
        // return this.bidArray.filter(bid => bid.hasValidPrice).map(bid => bid.price_currency)[0];
        // NEW - allows for bidArray to be empty after filters, in which case we return empty string - seems we don't have other 'correct' source here... so make sure caller can handle empty string
        // Sept 22 2022 - also handle both GRAND TOTAL and NORMAL rows
        let bidsToCheck = [];
        if (this.isGrandTotalRow) {
            bidsToCheck = this.bidArray.filter(bid => hasValueOrZero(bid.subtotal));  // only those that have a valid subtotal
        } else {
            // for NORMAL rows
            bidsToCheck = this.bidArray.filter(bid => bid?.hasValidPrice);
        }
        const validBidPricesCurrencies = bidsToCheck.map(bid => bid.price_currency);  // MAYBE EMPTY - array of currency codes for bids with a non-null price
        const defaultIfNoValidPrices = ''  // caller should be able to handle empty string
        return validBidPricesCurrencies.length ? validBidPricesCurrencies[0] : defaultIfNoValidPrices;
    }
    getAnalysisArray() {
        // returns array of objects only with { replyId, price, price_currency }, and only if they are for analysis (latest from each company)
        // analysis must be done on base currency for quotesheet in case different vendors quote in different currency 
        const forAnalysis = [];
        if (!this.priceCurrenciesValid) {
            return [];  // return empty array if currencies are not valid (any currency or just analysis ones???)
        }
        // HANDLE A NORMAL ROW (using 'price' for analysis) OR GRAND TOTAL (using 'subtotal' for analysis)
        for (const replyId in this.bidsForAnalysis) {
            const bidObj = this.bidsForAnalysis[replyId];
            // we put both 'price' and 'subtotal' into analysis object, then depending on whether its grand total row or not, we use different one in finding min/max above
            const analysisObj = { replyId, price: bidObj.price, price_currency: bidObj.price_currency, subtotal: bidObj.subtotal };
            forAnalysis.push(analysisObj);
        }
        return forAnalysis;
    }
    /* addBid(replyId, bidObj, isLatest) {  // bidObj should be instance of QuoteSheetReplyBid, OR null if there's no bid for this variant in this QuoteSheetReply
        // isLatest - boolean, true if this is the LATEST submission from the company who submitted it (one for each company)
        // use this to add a bid from a vendor after instantiating the row -- loop through each QuoteSheetReply and add them
        this.bids[replyId] = bidObj;  // should we use || or ?? to set a default???  depends on how your valueGetter works... or bidObj ? Object.freeze(bidObj) : bidObj;  // Object.freeze() returns the object, but bidObj might be null, so not an object
        // maybe you want to do certain validation of bids when you do this?  Instead of doing expensive things in getters...
        if (isLatest) {
            this.bidsForAnalysis[replyId] = bidObj;
        }
    } */
}

// Quote sheet chart renderer cell (button inside) 

var QuoteSheetRowChartHeaderRenderer = Vue.extend({  
    // renderer to use for the HEADER of 'show chart button' column
    template: `
        <div class="text-center">
            <b-icon icon="bar-chart-fill"></b-icon>
        </div>
    `
})

var QuoteSheetRowChartCellRenderer = Vue.extend({  
    // renderer to use for the 'show chart button' column
    // adds a button if the field is 'chartable'
    computed: {
        showChartButton() {
            // we have to make sure 'price' field was actually turned on for this quotesheet and variant... sometimes it could be turned off, even on a per-variant basis
            return this.params.data?.canShowChart;  // params.data is instance of CompareGridRow
        },
    },
    // mounted() {
    //     //console.log('mounted SmartFormItemCellRenderer - params:', this.params);
    // },
    methods: {
        triggerChart() {
            // call method on parent Vue component, using 'componentParent' passed in 'context'
            console.log('triggerChart', this.params);
            this.params.context.componentParent?.makeQuoteSheetChart(this.params.node.id);
        },
    },
    template: `
        <div class="d-flex align-items-center justify-content-center">
            <div>
                <b-link v-if="showChartButton" class="text-decoration-none" @click="triggerChart">
                    <b-icon icon="bar-chart-fill"></b-icon>
                </b-link>
            </div>
        </div>
    `
})

// FOR FORMIUM FORMS
class FormiumFormCompareGridRow {  // THE ACTUAL DATA OBJECT IN THE GRID for Formium Forms
    constructor({ // import all these things from quotesheet-reply-helpers
        formium_item,  // REQUIRED pass in object representing FormiumItem - currently just a plain JS object
    }) {
        this.id = formium_item.id;  // then use gridOptions.getRowNodeId() to use this as the ID in the grid too
        this.groupId = formium_item.id;  // do this because I think we have to use groups on all rows if we use them for QuoteSheets?
        this.formium_item = Object.freeze(formium_item);  
        // we will not add replies in constructor, but we'll add top level key for 'replies' and add them later
        this.replies = {};  // this should be an OBJECT, keys will be the FormiumReply.id, values will be the individual answers to questions (values)
    }
    // GETTERS
    get label() {  // get the text of the 'question' to display
        return this.formium_item.label;
    }
    get possibleChoices() {  // TODO - make this a helper!
        // always return an array here -- if no choices, then it will be empty
        // formium_item.choices is currently always a string, even if a list... comma separated, with spaces 
        const rawChoices = this.formium_item.choices ?? '';  // if nullish, coerce to empty string for consistency in next step
        // if we already have an array of choices from backend, just use that
        if (rawChoices && Array.isArray(rawChoices)) {
            return rawChoices;  // returns array if we had one
        }
        // otherwise we're most likely dealing with a string, so turn it into an array -- or if empty string, return empty array
        const trimmedString = rawChoices.trim();  // trim first, before we do boolean check (because "  " is truthy)
        if (!trimmedString) {
            return [];  // return empty array if we had no choices -- otherwise next step on empty string returns array with 1 element (empty string)
        }
        const choicesArray = trimmedString.split(',').map(ch => ch.trim());  // split then trim to make clean array
        return choicesArray;
    }
    get hasChoices() {
        // return Boolean if we have choices for this row or not
        return this.possibleChoices.length > 0;
    }
    // METHODS
    isValidChoice(value) {
        // return Boolean, if value is a valid choice for this question
        return this.possibleChoices.includes(value);
    }
    addReply(replyId, answerValue) {
        // used to add replies / answers -- done after instance is created so we can loop through all replies and add them one after another
        // TODO - provide complete answerObj with value inside instead of just plain answerValue?
        this.replies[replyId] = answerValue;
    }
}


var FormiumValueCellRenderer = Vue.extend({  // special renderer to use for all formium form ANSWERS to control padding, etc.
    computed: {
        trimmedValue: function() {  // just for testing...
            return isString(this.params.value) ? this.params.value.trim() : this.params.value;
        },
    },
    template: `
        <div class="py-2 text-show-breaks">
            {{ params.value }}
        </div>
    `
})


// FOR SMARTFORMS

class SmartFormCompareGridRow {  // THE ACTUAL DATA OBJECT IN THE GRID 
    // NOTE - this is used BOTH in the compare grid component, and the SmartFormReplyView component, for consistency
    constructor({ 
        smartform_field,  // REQUIRED pass in object representing SmartFormField - currently just a plain JS object
        replies,  // now also passing in a list of all the full reply objects -- we won't stash all of them, but get stuff from them in constructor so we don't have to do the addReply() thing later and use getters
    }) {
        this.id = smartform_field.id;  // then use gridOptions.getRowNodeId() to use this as the ID in the grid too
        this.groupId = smartform_field.id;  // do this because I think we have to use groups on all rows if we use them for QuoteSheets?
        this.expand = smartform_field.expand ?? false;  // should be boolean, but undefined will mean false - only applies to group rows in tree data 
        this.slug = smartform_field.slug ?? smartform_field.id;  // for our grouping rows, no slug
        this.smartform_field = Object.freeze(smartform_field);  
        this.label = this.smartform_field.label;
        this.possibleChoices = []; // TODO LATER
        this.hasChoices = this.possibleChoices.length > 0;
        this.canShowChart = this.smartform_field.is_numeric && (this.smartform_field.is_reply_input || this.smartform_field.is_calculated);  // can we / should we show a chart comparing the replies for this item?
        this.valueStringFormatter = (val) => {  // make a formatter function to go along for the ride
            return val ? formatSmartFormValue(val, smartform_field) : '';
        }
        // NEW THINGS USING LiveSmartForm FOR SERIALIZER
        this.form_id = smartform_field.form_id;  // convenient to have for figuring out which table to put it in for review component
        this.data_path = smartform_field.data_path ?? null;  // let it be null - that means no data for this one
        this.nested_path = smartform_field.nested_path ?? [];  // used for TreeData structure - generated on backend - array of strings
        this.custom_classes = smartform_field.custom_classes ?? [];  // same custom_classes we use in smartform form itself - array of CSS classes
        // NOW DEAL WITH REPLIES & ANALYSIS OF VALUES
        const showValue = !!this.data_path && !smartform_field.is_data_parent && !!smartform_field.is_input;  // must be input and NOT have nested data children
        this.replies = {};  // will be an OBJECT, { replyId: value, ...  }
        this.enableAnalysisForRow = this.smartform_field.is_money && this.smartform_field.type_slug === "money_fixed_currency";
        this.repliesForAnalysis = [];  // ARRAY of objects with 2 items each [ {replyId, value}, {replyId, value} ]
        replies.forEach(rep => {
            const replyId = rep.id;
            const isLatest = rep.is_latest;  // is this the latest reply from the company who submitted it?  We only consider these for analysis cols
            // const value = showValue ? rep.smartform_reply_data[this.slug] : '';  
            const defaultValue = '';
            const value = showValue ? get(rep.smartform_reply_data, this.data_path, defaultValue) : defaultValue; 
            // stores the value from each reply in this row for easy access
            this.replies[replyId] = value;
            if (this.enableAnalysisForRow && isLatest && ![null, undefined, ''].includes(value)) {  // got a non-empty numerical value?  use it in analysis
                this.repliesForAnalysis.push({replyId, value});
            }
        });
        this.replyValues = Object.values(this.replies);  // ALL values
        const analysisValues = this.repliesForAnalysis.map(r => r.value);  // just the values for analysis
        // we'll make sure there are more than 1 UNIQUE values, in order to highlight min/max
        const notAllSame = uniq(analysisValues).length > 1;
        //this.hasMultipleAnalysisValues = this.repliesForAnalysis && this.repliesForAnalysis.length > 1;
        const minAnalysisReply = minBy(this.repliesForAnalysis, 'value');  // returns undefined if array is empty, which is OK
        this.minReplyId = notAllSame && minAnalysisReply ? minAnalysisReply.replyId : undefined;
        this.minValue = minAnalysisReply ? minAnalysisReply.value : undefined;
        this.minBidPrice = this.valueStringFormatter(this.minValue);  // using the same name as quotesheets for now
        const maxAnalysisReply = maxBy(this.repliesForAnalysis, 'value');  // returns undefined if array is empty, which is OK
        this.maxReplyId = notAllSame && maxAnalysisReply ? maxAnalysisReply.replyId : undefined;
        this.maxValue = maxAnalysisReply ? maxAnalysisReply.value : undefined;
        this.maxBidPrice = this.valueStringFormatter(this.maxValue);  // using the same name as quotesheets for now
        this.meanValue = mean(this.repliesForAnalysis.map(ro => ro.value));
        this.meanBidPrice = this.valueStringFormatter(this.meanValue);  // using the same name as quotesheets for now
    }
    // METHODS
    isValidChoice(value) {
        // return Boolean, if value is a valid choice for this question
        return this.possibleChoices.includes(value);
    }
    addReply(replyId, answerValue) {
        // used to add replies / answers -- done after instance is created so we can loop through all replies and add them one after another
        // TODO - provide complete answerObj with value inside instead of just plain answerValue?
        this.replies[replyId] = answerValue;
    }
}


var SmartFormValueCellRenderer = Vue.extend({  // special renderer to use for all formium form ANSWERS to control padding, etc.
    computed: {
        trimmedValue: function() {  // just for testing...
            return isString(this.params.value) ? this.params.value.trim() : this.params.value;
        },
    },
    template: `
        <div class="py-2 text-show-breaks">
            {{ params.value }}
        </div>
    `
})

var SmartFormItemCellRenderer = Vue.extend({  
    // renderer to use for the 'Item' column - the left column with labels for each SmartFormField
    // adds a button if the field is 'chartable'
    // updated Sept 3 2022 to work with rows being shown as treeData
    // any 'filled' rows, added by the grid just for grouping the treeData will not have params.data or params.node.data
    // so if we have those, we'll render the value itself.... our valueGetter determines the value
    computed: {
        showChartButton() {
            return this.params.data?.canShowChart;
        },
    },
    mounted() {
        //console.log('mounted SmartFormItemCellRenderer - params:', this.params);
    },
    methods: {
        triggerChart() {
            // call method on parent Vue component, using 'componentParent' passed in 'context'
            console.log('triggerChart', this.params);
            this.params.context.componentParent?.makeSmartFormChart(this.params.node.id);
        },
    },
    template: `
        <div class="d-flex align-items-center justify-content-between">
            <div>{{ params.value }}</div>
            <div>
                <b-link v-if="showChartButton" class="text-decoration-none" @click="triggerChart">
                    <b-icon icon="bar-chart-fill"></b-icon>
                </b-link>
            </div>
        </div>
    `
})


// FOR DOCREQUESTS

const docRequestRows = [
    // Array of objects that define the rows we want to show for docrequests
    // for cellRenderer, either just use null, or object with {component: , params: }
    {
        dataKey: 'download_url',
        label: 'Download Link',
        showInCompareGrid: false,
        valueStringFormatter: (value) => value,
        cellRenderer: {
            component: 'downloadLinkRenderer',
            params: {  // extra params to pass to component
                linkText: 'Download File',
            },
        },  // to be used by cellRendererSelector
    },
    // OLD VERSION - for single upload file only
    // {
    //     dataKey: 'upload', // the complete nested upload object - use this to render FileDownloadBadge component
    //     label: 'File',
    //     showInCompareGrid: true,
    //     valueStringFormatter: (value) => value,
    //     cellRenderer: {
    //         component: 'fileDownloadBadgeRenderer',  // ag-grid friendly wrapper around my normal FileDownloadBadge component
    //         params: {}, // put any extra params here for the component
    //     },
    // },
    // NEW VERSION - for multiple files - will be in 'active_files' - use other renderer component for this
    {
        dataKey: 'active_files', // array of full s3file objects - pass this as value of the renderer component
        label: 'File(s)',
        showInCompareGrid: true,
        valueStringFormatter: (value) => value,
        cellRenderer: {
            component: 'multipleFileDownloadButtonRenderer',
            params: {}, // put any extra params here for the component
        },
    },
    {
        // NOTE: I don't think we use this one anywhere anymore... as of Jan 2025
        dataKey: 'filesize',
        label: 'File size',
        showInCompareGrid: false,
        valueStringFormatter: (value) => FormatFileSizeHelper(value),
        cellRenderer: null,
    },
    {
        dataKey: 'comment', 
        label: 'Comment',
        showInCompareGrid: true, // we filter on this before determining what to show in ag-grid CompareGrid
        valueStringFormatter: (value) => value,
        cellRenderer: null,
    },
];

const docRequestValueFormatterMap = new Map(docRequestRows.map(drr => [drr.dataKey, drr.valueStringFormatter]));  // Map() of docRequestRow.dataKey: valueStringFormatter function (pass in the value)
const docRequestValueCellRendererMap = new Map(docRequestRows.map(drr => [drr.dataKey, drr.cellRenderer]));  // Map() of docRequestRow.dataKey: cellRenderer function (pass in the value)

// we'll make one consistent row structure, but set a 'dataKey' for each row, which is the thing in the docrequest that this row represents -- pass that in when instantiating
class DocRequestCompareGridRow {  // THE ACTUAL DATA OBJECT IN THE GRID for DocRequests
    constructor({ // import all these things from quotesheet-reply-helpers
        dataKey,  // REQUIRED - pass in a string for what this represents -- should match with a key in the reply object (use this to pull data from each reply)
        label, // REQUIRED - what we'll show as the item/description for this row
        docrequest,  // REQUIRED pass in object representing DocRequest - currently just a plain JS object
    }) {
        this.id = dataKey; 
        this.dataKey = dataKey;  // a bit redundant... but maybe you want to change later
        this.groupId = dataKey;  // do this because I think we have to use groups on all rows if we use them for QuoteSheets?
        this.label = label;  // show this in the row
        this.docrequest = Object.freeze(docrequest);  
        // we will not add replies in constructor, but we'll add top level key for 'replies' and add them later
        this.replies = {};  // this should be an OBJECT, keys will be the DocRequestReply.id, values will be the docrequest_reply_data object
    }
    // GETTERS
    // METHODS
    addReply(replyId, drReplyDataObj) {
        // used to add replies -- done after instance is created so we can loop through all replies and add them one after another
        // drReplyDataObj has keys 'comment' and 'active_files' (array of objects of s3file data)
        this.replies[replyId] = drReplyDataObj;
    }
    getReplyData(replyId) {
        // use this in valueGetter in the grid, just pass in the reply ID you want to get data for
        // this knows which dataKey to get from this.dataKey, will fetch the value from the nested replyData object
        const replyData = this.replies[replyId];  // will return undefined if not found
        if (!replyData) return '';
        if (this.dataKey === 'comment') {  // comment is top-level in replyData, others are nested in replyData.upload
            return replyData.comment || '-';  
        } else if (this.dataKey === 'upload') {  // WILL NOT BE USED ANYMORE... replaced by 'active_files'
            return replyData.upload;  // if dataKey is upload, return the complete nested upload object
        } else if (this.dataKey === 'active_files') {
            return replyData.active_files;  // if dataKey is active_files, return the array of full s3file objects
        }
        // if not comment or upload, then we must be getting things from nested inside replyData.upload
        // NOTE - this doesn't really work for multiple files... we'll have to change this to handle that, but maybe not needed (maybe we don't have a need to get stuff from inside upload data anymore...)
        const uploadData = null;  // replyData.upload;  // setting to null for now, since we don't have this anymore  (Jan 2025)
        if (!uploadData) {
            return '';
        }
        // below will not run if uploadData is null... but keeping it here for now since we may need it back later
        const rawValue = uploadData[this.dataKey];  // this is the raw data... now we'll format in here, so no need to call separate formatter
        const valueStringFormatter = docRequestValueFormatterMap.get(this.dataKey);  // will return the function
        return valueStringFormatter(rawValue);
    }
}



// COMPONENTS

var VendorReplyColGroupHeader = Vue.extend({  // the column group header component
    template: `
        <div class="ag-header-group-cell-label vendor-response-area-header justify-content-center">
            <div class="ag-header-group-text d-block text-center text-wrap p-1"> <!-- HEIGHT ADJUSTS BASED ON THIS -->
                <div class="vendor-response-area-heading">{{ params.displayName }}</div>
                <!-- <div class="font-weight-normal text-secondary my-1" style="font-size: 11px;">Submitted {{ submitDate }}</div> -->
                <b-button size="sm" variant="subtle-primary" class="my-1" @click="openActions('')">View Response</b-button>
                <v-popover
                    offset="8"
                    :open.sync="isOpen"
                    auto-hide
                    :handleResize="false"
                    placement="right"
                    class="d-inline-block my-1"
                    popover-inner-class="popover-inner"
                >  
                    <!-- This will be the popover target (for the events and position) -->
                    <b-button size="sm" variant="subtle-primary" class="tooltip-target">
                        <b-icon icon="three-dots" scale="1"></b-icon>
                    </b-button>
                
                    <!-- This will be the content of the popover -->
                    <template slot="popover" class="p-0">
                        <div class="d-flex flex-column">
                            <a v-close-popover @click="openActions('externalComments')" href="#" class="font-size-80 font-weight-500 dropdown-item">Send Message</a>
                            <a v-close-popover @click="openActions('internalComments')" href="#" class="font-size-80 font-weight-500 dropdown-item">Add Comment</a>
                        </div>
                    </template>
                </v-popover>
                <div v-if="params.hasMultipleReplies" class="mt-1 d-flex justify-content-center gap-3 font-size-70">
                    <b-link @click="showAll">Show All</b-link>
                    <b-link @click="showLatest">Show Latest</b-link>
                </div>
            </div>
        </div>
    `,
    data: function() {
        return {
            isOpen: false,  // using .sync with open prop on v-popover
        }
    },
    computed: {
        /* submitDate: function() {
            return FormatDateHelper(this.params.reply.submitted_on);
        }, */
    },
    methods: {
        openActions: function(initialTab) {
            // emit event to tell parent to open modal for this reply/vendor - with optional string of which 'initial tab' to go to
            // changed on June 13 2022 to pass up the company ID only -- not the reply object -- so we can have colGroups just for a company, with multiple responses inside
            //console.log('from col group header, groupId:');
            const companyId = this.params.companyId;
            console.log('companyId', companyId);
            console.log('params', this.params);
            this.params.componentParent.openActionModal(companyId, initialTab);  // pass up the companyId, and the initialTab to go to (optional)
        },
        showAll() {
            // use method in parent component to show all replies for this company 
            this.params.componentParent.showAllRepliesForCompany(this.params.companyId);
        },
        showLatest() {
            this.params.componentParent.showOnlyLatestReplyForCompany(this.params.companyId);
        }
    },
});

var SingleReplyHeaderRenderer = Vue.extend({  // the header (group or just cell) for a single reply for a company (the cell under the main company cell)
    template: `
        <div class="text-center w-100">
            <div>Submitted on {{ params.submittedOnString }}</div>
            <div
                class="mt-1 font-weight-500 text-uppercase font-size-70" 
                :class="params.isLatest ? 'text-success' : 'text-muted'"
            >
                {{ params.isLatest ? 'Latest Version' : 'Previous Version'}}
            </div>
        </div>
    `,
});

var AnalysisColGroupHeader = Vue.extend({  // for use as headerComponent or headerGroupComponent, takes extra param of 'subheading' (headerComponentParams: { subheading: 'My subheading'})
    template: `
        <div class="ag-header-group-cell-label w-100 d-flex flex-column align-items-start justify-content-center">
            <div class="ag-header-group-text">{{ params.displayName }}</div>
            <div
                class="mt-1 font-weight-500 font-size-80 text-muted" 
            >
                {{ params.subheading }}
            </div>
        </div>
    `,
});


export {
    grandTotalVariantId,
    CompareGridRow,
    QuoteSheetRowChartHeaderRenderer,
    QuoteSheetRowChartCellRenderer,
    FormiumFormCompareGridRow,
    FormiumValueCellRenderer,
    DocRequestCompareGridRow,
    docRequestRows,
    docRequestValueCellRendererMap,
    VendorReplyColGroupHeader,
    SingleReplyHeaderRenderer,
    SmartFormCompareGridRow,
    SmartFormValueCellRenderer,
    SmartFormItemCellRenderer,
    AnalysisColGroupHeader,
}